import { Box, Popover, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { numberToUSDCurrency } from '../helpers/numberToUSDCurrency';
import { NetSettlementStatus, SettlementPaymentDetails } from '../types';


const useStyles = makeStyles()((theme: Theme) => ({
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    pointerEvents: 'auto',
    padding: '16px',
    background: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    width: '280px',
  },
  header: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '12px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  label: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  value: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  divider: {
    height: '1px',
    background: theme.palette.primary.main,
    margin: '8px 0',
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  footerLabel: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  footerValue: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  amountCenter: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
    flex: 1,
    textAlign: 'center',
  },
  statusBadge: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

// Type for individual distribution items from SettlementPaymentDetails  
type SettlementDistributionItem = NonNullable<SettlementPaymentDetails['distributions']>[number];

interface DetailsHoverCardProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  distribution: SettlementDistributionItem | null;
  total?: number;
  nonNegotiationAmountToClient?: number;
  netSettlementStatus?: NetSettlementStatus;
}

const DetailsHoverCard = ({
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  distribution,  
}: DetailsHoverCardProps) => {
  const styles = useStyles();


  return (
    <Popover
      className={styles.classes.popover}
      classes={{
        paper: styles.classes.paper,
      }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      disableRestoreFocus
      slotProps={{
        paper: {
          onMouseEnter: onMouseEnter,
          onMouseLeave: onClose,
        },
      }}
    >
      <Box>

        {/* First Row with divider below */}
        <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Initial Net Settlement to Client</Typography>
          <Typography className={styles.classes.value}>
            {numberToUSDCurrency(distribution?.nonNegotiationAmountToClient ?? 0)}
          </Typography>
        </Box>



        {/* Breakdown Rows */}
        {(distribution?.liensNegotiationSavings ?? 0) > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>{'Liens - Saved by negotiation'}</Typography>
          <Typography className={styles.classes.value}>
            {numberToUSDCurrency(distribution?.liensNegotiationSavings)}
          </Typography>
        </Box>}

        {(distribution?.serviceProvidersNegotiationSavings ?? 0) > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>{'Service Providers - Saved by negotiation'}</Typography>
          <Typography className={styles.classes.value}>
            {numberToUSDCurrency(distribution?.serviceProvidersNegotiationSavings)}
          </Typography>
        </Box>}

        {(distribution?.advancesNegotiationSavings ?? 0) > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>{'Expenses - Saved by negotiation'}</Typography>
          <Typography className={styles.classes.value}>
            {numberToUSDCurrency(distribution?.advancesNegotiationSavings)}
          </Typography>
        </Box>}




      </Box>
    </Popover>
  );
};

export default DetailsHoverCard;
